import Service, { service } from '@ember/service';
import { variation } from 'ember-launch-darkly';
import { dropTask } from 'ember-concurrency';
import config from 'my-phorest/config/environment';
import issueZendeskSsoTokenMutation from 'my-phorest/gql/mutations/issue-zendesk-sso-token.graphql';
import issueZendeskMessagingTokenMutation from 'my-phorest/gql/mutations/issue-zendesk-messaging-token.graphql';

export default class ZendeskService extends Service {
  @service('browser/window') window;
  @service tagManager;
  @service apollo;
  @service intl;
  @service notifications;
  @service session;
  @service errorHandler;

  isInitializing = false;
  widgetInitialized = false;
  isMessengerOpen = false;

  identify() {
    this.track({ event: 'Zendesk Identify' });
  }

  track(data) {
    if (!this.hasInitialized) {
      this.initialize();
    }
    this.tagManager.push(data);
  }

  initialize() {
    if (this.isInitializing || this.hasInitialized) {
      return;
    }
    this.isInitializing = true;
    this.track({ event: 'Zendesk Initialize' });
    this.isInitializing = false;
  }

  logout() {
    if (this.hasInitialized && variation('release-zendesk-messaging')) {
      try {
        this.window.zE('messenger', 'close');
        this.window.zE('messenger', 'hide');
        this.window.zE('messenger', 'logoutUser');
        this.widgetInitialized = false;
        this.isMessengerOpen = false;
      } catch (error) {
        console.warn('Failed to logout Zendesk widget:', error);
      }
    }
  }

  hide() {
    if (this.hasInitialized && variation('release-zendesk-messaging')) {
      try {
        this.window.zE('messenger', 'close');
        this.window.zE('messenger', 'hide');
        this.isMessengerOpen = false;
      } catch (error) {
        console.warn('Failed to hide Zendesk widget:', error);
      }
    }
  }

  show() {
    if (this.hasInitialized && variation('release-zendesk-messaging')) {
      try {
        this.window.zE('messenger', 'show');
      } catch (error) {
        console.warn('Failed to show Zendesk widget:', error);
      }
    }
  }

  get hasInitialized() {
    return !!this.window.zE;
  }

  get widgetKey() {
    if (!variation('release-zendesk-sandbox-support')) {
      return config.zendesk.classicKey;
    }
    return config.zendesk.sandBoxKey;
  }

  get currentUserName() {
    return this.session.user?.name || this.session.branch?.fullName;
  }

  get branchAccountId() {
    return this.session.branch?.accountId;
  }

  get currentUserEmail() {
    return this.session.user?.email;
  }

  @dropTask
  *initializeMessagingWidgetTask() {
    if (!variation('release-zendesk-messaging')) {
      return;
    }

    if (this.widgetInitialized) {
      return;
    }

    this.widgetInitialized = true;

    let messagingToken = null;
    try {
      messagingToken = yield this.#fetchMessagingToken();
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }

    this.#configureWidgetSettings();

    if (!this.hasInitialized) {
      yield this.#loadWidgetScript();
    }

    this.#setupMessagingWidget(messagingToken, false);
  }

  async showAndOpenMessaging() {
    if (!variation('release-zendesk-messaging')) {
      return;
    }

    try {
      if (!this.hasInitialized) {
        await this.initializeMessagingWidgetTask.perform();
      }

      this.window.zE('messenger', 'show');
      this.window.zE('messenger', 'open');
      this.isMessengerOpen = true;
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  @dropTask
  *issueZendeskToken() {
    return yield this.apollo.mutate(
      {
        mutation: issueZendeskSsoTokenMutation,
      },
      'issueZendeskSsoToken'
    );
  }

  @dropTask
  *issueMessagingToken() {
    return yield this.apollo.mutate(
      {
        mutation: issueZendeskMessagingTokenMutation,
      },
      'issueZendeskMessagingToken'
    );
  }

  async #fetchMessagingToken() {
    await this.issueZendeskToken.perform();
    const result = await this.issueMessagingToken.perform();

    return result.tokens?.find(
      (token) => token.businessId === this.session.businessId
    )?.accessToken;
  }

  #configureWidgetSettings() {
    this.window.zESettings = {
      webWidget: {
        color: { theme: '#115E59' },
        contactForm: {
          suppress: false,
          attachments: false,
          ticketForms: [{ id: config.zendesk.ticketFormId }],
          fields: [
            { id: 'name', prefill: { '*': this.currentUserName } },
            {
              id: 'email',
              prefill: {
                '*': this.currentUserEmail,
              },
            },
            { id: 360008170354, prefill: { '*': this.branchAccountId } },
          ],
        },
        helpCenter: { suppress: false },
      },
    };
  }

  #loadWidgetScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${this.widgetKey}`;

      let timeoutId;

      const handleError = () => {
        clearTimeout(timeoutId);
        reject(new Error('Failed to load Zendesk widget script'));
      };

      timeoutId = setTimeout(() => {
        if (typeof this.window.zE !== 'function') {
          handleError();
        }
      }, 10000); // 10 second timeout for background loading

      script.onload = () => {
        this.window.zE('messenger', 'hide');

        const intervalId = setInterval(() => {
          if (typeof this.window.zE === 'function') {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
            resolve();
          }
        }, 100);
      };

      script.onerror = handleError;

      document.body.appendChild(script);
    });
  }

  #setupMessagingWidget(messagingToken, shouldOpen = true) {
    this.window.zE('messenger:on', 'open', () => {
      this.isMessengerOpen = true;
    });

    this.window.zE('messenger:on', 'close', () => {
      this.isMessengerOpen = false;
      this.window.zE('messenger', 'hide');
    });

    this.window.zE('messenger:on', 'unreadMessages', (count) => {
      if (count > 0 && !this.isMessengerOpen) {
        // Minimize the widget before it's shown so that it appears in a minimized state
        // Only do this if the messenger is not already open to avoid interrupting the user
        this.window.zE('messenger', 'close');
        this.window.zE('messenger', 'show');
      }
    });

    if (messagingToken) {
      this.window.zE('messenger', 'loginUser', (callback) => {
        callback(messagingToken);
      });
    }

    if (shouldOpen) {
      this.window.zE('messenger', 'open');
      this.isMessengerOpen = true;
    }
  }
}
